"use strict"

import React, { useEffect, useState } from "react"
import { Modal, Button } from "react-bootstrap"
import { FormattedMessage, useIntl } from "react-intl"
import PropTypes from "prop-types"
import moment from "moment"

import { API_STATUS } from "../../../../shared_components/widgets/utils"
import PageLoadingPanel from "../../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../../shared_components/widgets/PageErrorPanel"
import { logToServer } from "../../../../ErrorBoundary"
import { withSampleConfig } from "../../../../config/SampleConfigProvider"
import { addNewObject } from "../../../../Loaders"
import { isNumber } from "../../../../shared_components/utils"
import { MethodColumnSpanner } from "../../resources/MethodColumnSpanner"
import { testItemColumns, getRowSpanNum } from "../../SampleScheduleTab/SampleSchedule"
import CancelMethodButton from "../../resources/CancelMethodButton"
import ToggleMethodStatus from "../../resources/ToggleMethodStatus"
import { getFormattedMessage } from "../../../shared_components_registration"

const ModalEnterData = ({
  sample_current_data,
  sample_source_types,
  stopSampleRound,
  updateMetrics,
  sample_current_data_status,
  sample_round_stop_status,
  isNSView,
  sample,
  show,
  onHide,
  onSuccess,
}) => {
  const intl = useIntl()
  const date_format = "YYYY-MM-DDTHH:mm:ss"
  const [status_init, setStatusInit] = useState(null)
  const [status_save, setStatusSave] = useState(null)
  const [data, setData] = useState({})
  const [submissionData, setSubmissionData] = useState({})
  const [row_span, setRowSpan] = useState(1)
  const [firstMethod, setFirstMethod] = useState("-")

  useEffect(() => {
    onInitialLoad()
  }, [])

  useEffect(() => {
    // Merge current test_item Observation values into the data
    if (data?.sampling_point?.methods) {
      let smp_data = Object.assign({}, data)
      const filteredMethods = {}
      Object.entries(data.sampling_point.methods)
        .filter(([m, _]) => data.analyzable_methods.includes(m))
        .forEach(([key, method_info], i) => {
          i === 0 && setFirstMethod(key)
          filteredMethods[key] = method_info
          method_info?.["test_items"] &&
            method_info["test_items"].forEach((ti) => {
              const current_value = sample_current_data.find((t) => t.id === ti.id)
              ti["value"] = isNumber(current_value?.value) ? current_value.value : ""
            })
        })
      const row_span = getRowSpanNum({ ...smp_data.sampling_point, methods: filteredMethods })
      setRowSpan(row_span)
      setData(smp_data)
    }
  }, [sample_current_data])

  useEffect(() => {
    setStatusInit(sample_current_data_status)
  }, [sample_current_data_status])

  const onInitialLoad = () => {
    // --- Pre-Process Sample Info ---
    let smp = Object.assign({}, sample)
    smp.has_sampling_time = smp.sampling_time != null
    smp.sampling_time = (!smp.has_sampling_time ? moment() : moment(smp.sampling_time))
      .local()
      .format(date_format)
    smp.sample_source_type = smp.sample_source_type || {}

    if (!smp.has_sampling_time) {
      smp.sample_source_type_id = smp.sampling_point["default_sample_source_type"]
    }

    setData(smp)
  }

  const onChange_sampleValue = (event) => {
    let smp_data = Object.assign({}, data)
    smp_data[event.target.name] = event.target.value
    setData(smp_data)
  }

  const triggerAlert = (id) => {
    alert(
      intl.formatMessage({
        id: id,
      }),
    )
  }

  const submitSampleData = (data) => {
    addNewObject(`/laboratory/sample-management/sample/${data.id}/save_sample_data/`, data)
      .then((_) => {
        onSuccess()
      })
      .catch((error) => {
        console.error(error)
        logToServer(error)
        triggerAlert("samplemanagement.error.unable.to.save.data")
        setStatusSave(API_STATUS.ERROR)
      })
  }

  const saveSampleData = (event) => {
    event.preventDefault()

    const test_items_with_data = Object.entries(data.sampling_point.methods)
      .filter(([m, _]) => data.analyzable_methods.includes(m))
      .map(([m, method]) => method.test_items)
      .flat()
      .filter((item) => item.value !== "")

    if (
      !confirm(
        intl.formatMessage({
          id: "samplemanagement.confirm.save.sample.data",
        }),
      )
    ) {
      return false
    }

    // --- Prepare data ---
    let postdata = {
      id: data.id,
      sampling_point: {
        testitem_set: test_items_with_data.map((ti) => ({
          id: ti.id,
          value: ti.value,
        })),
      },
    }

    if (!data.has_sampling_time) {
      postdata["sampling_time"] = moment(data.sampling_time).format(date_format)
      postdata["sample_source_type_id"] = data.sample_source_type_id
    }

    setSubmissionData(postdata)
    // --- /Prepare data ---

    // ----- POST -----
    setStatusSave(API_STATUS.LOADING)
    if (!data.has_sampling_time) {
      // Stop round (1) and upload data (2)
      // (1) Try to stop round (will upload data on round stop success)
      stopSampleRound(data["sample_round"], {
        id: data["sample_round"],
        samples: [postdata],
      })
    } else {
      // Upload data only
      submitSampleData(postdata)
    }
    // ----- / POST -----
  }

  useEffect(() => {
    if (sample_round_stop_status === API_STATUS.ERROR) {
      triggerAlert("samplemanagement.error.unable.to.save.data")
      setStatusSave(sample_round_stop_status)
    } else if (sample_round_stop_status === API_STATUS.SUCCESS) {
      updateMetrics({ sample_round_stop_status: API_STATUS.IDLING })
      setStatusSave(sample_round_stop_status)
      // (2) Upload Sample Data
      submitSampleData(submissionData)
    }
  }, [submissionData, sample_round_stop_status])

  const init_status = {
    sample_current_data: {},
    sample_current_data_status: API_STATUS.LOADING,
    sample_round_stop_status: API_STATUS.IDLING,
  }

  // Table Headers AND Data Row Accessors
  const smp_data_map = [
    {
      id: "general.sample",
      label: "Sample",
      accessor: (smp) => {
        if (smp.alias) {
          return (
            <>
              {smp.sample_id}
              <br />
              <span style={{ fontSize: "12px" }}>{`(${smp.alias})`}</span>
            </>
          )
        } else {
          return smp.sample_id
        }
      },
    },
    {
      id: "samplemanagement.sample.connection.number",
      label: "Sample Connection Number",
      accessor: (smp) => smp.sampling_point["sample_conn_no"],
    },
    {
      id: "general.reactor",
      label: "Reactor",
      style: { minWidth: "150px" },
      accessor: (smp) => {
        return (
          <div>
            {smp.sampling_point["reactor_list"].map((src) => (
              <div key={`dataSmp${smp?.id}${src.name}`}>
                {`${src.name} (${smp?.data_runs?.[src.name]?.name || "-"})`}
              </div>
            ))}
          </div>
        )
      },
    },
  ]

  const tiDetailHeaders = isNSView ? [{ id: "general.target.value", label: "Target Value" }] : []

  // Table Headers Only (Data Rows Handled Individually Below)
  const smp_header_map = [
    {
      id: "general.tag",
      style: { minWidth: "100px" },
      label: "Tag",
    },
    {
      id: "samplemanagement.test.item",
      style: { minWidth: "100px" },
      label: "Test Item",
    },
    ...tiDetailHeaders,
    {
      id: "general.value",
      label: "Value",
      style: { minWidth: "150px" },
      extra_text: "*",
    },
    {
      id: "samplemanagement.edit.methods",
      label: "Edit Methods",
      style: { minWidth: "150px" },
    },
    {
      id: "samplemanagement.sample.source.type",
      label: "Sample Source Type",
      style: { minWidth: "120px" },
      extra_text: "*",
    },
    {
      id: "general.time",
      label: "Time",
      extra_text: "*",
    },
  ]

  const getTableHeader = (metric, idx) => (
    <th key={`header_${idx}`} className={"text-center"} style={metric["style"] || { minWidth: "60px" }}>
      {getFormattedMessage(metric["id"], metric["label"])}
      {metric["extra_text"] && metric["extra_text"]}
    </th>
  )

  const methodSpannerParams = (name) => ({
    name: name,
    colSpan: isNSView ? 4 : 3,
    method_info: data?.sampling_point?.methods?.[name],
    onMethodClick: () => {
      let smp_data = { ...data }
      smp_data["sampling_point"]["methods"][name]["open"] = !data["sampling_point"]["methods"][name]["open"]
      const row_span = getRowSpanNum(smp_data["sampling_point"])
      setRowSpan(row_span)
      setData(smp_data)
    },
  })

  const MethodActionsColumn = ({ method }) => {
    const actionProps = {
      methodInfo: data.methods_status?.[method],
      name: method,
      smpID: data.id,
      isLoading: status_save === API_STATUS.LOADING,
      onSuccess: onSuccess,
      onError: () => setStatusSave(API_STATUS.ERROR),
    }
    return (
      <td className={"text-center"}>
        <ToggleMethodStatus {...actionProps} />
        <CancelMethodButton isHistorical={data.historical} {...actionProps} />
      </td>
    )
  }

  return (
    <Modal
      show={show}
      onHide={() => {
        onHide()
        updateMetrics(init_status)
      }}
      backdrop={"static"}
      keyboard={false}
      style={{ overflowX: "scroll", overflowY: "scroll" }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={"fa fa-retweet"} />
          &nbsp;&nbsp;
          {`${getFormattedMessage(
            "samplemanagement.modal.title.upload_data_for_sample",
            "Upload Data for Sample",
            true,
            intl,
          )} ${data.sample_id} (#${data.id})`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {status_init === API_STATUS.LOADING && <PageLoadingPanel />}

        {status_init === API_STATUS.ERROR && <PageErrorPanel attemptReload={onInitialLoad} />}

        {status_init === API_STATUS.SUCCESS && (
          <>
            <form name={"form_enterSampleData"} onSubmit={saveSampleData}>
              <table className={"table table-bordered table-sm tbl_lanzatable"}>
                <thead>
                  <tr>
                    {[...smp_data_map, ...smp_header_map].map((metric, idx) => getTableHeader(metric, idx))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {smp_data_map.map((static_metric, idx) => (
                      <td key={`static_row_${idx}`} rowSpan={row_span} className={"text-center"}>
                        {static_metric["accessor"](data)}
                      </td>
                    ))}
                    <MethodColumnSpanner {...methodSpannerParams(firstMethod)} />
                    <MethodActionsColumn method={data.sampling_point.methods[firstMethod]?.name} />
                    <td rowSpan={row_span} className={"text-center"}>
                      <select
                        className={"form-select form-select-sm"}
                        name={"sample_source_type_id"}
                        value={data.sample_source_type_id}
                        disabled={!data.historical || data.has_sampling_time}
                        onChange={(evt) => onChange_sampleValue(evt)}
                        required
                      >
                        <option value={data.sample_source_type.name || ""}>
                          {data.sample_source_type.name
                            ? data.sample_source_type.name
                            : intl.formatMessage({
                                id: "general.dropdown.please.select",
                              })}
                        </option>
                        {(sample_source_types || []).map((ch, k) => {
                          return (
                            <option key={k} value={ch.value}>
                              {ch.display_name}
                            </option>
                          )
                        })}
                      </select>
                    </td>
                    <td rowSpan={row_span} className={"text-center"}>
                      <input
                        type={"datetime-local"}
                        className={"form-control form-control-sm text-center"}
                        name={"sampling_time"}
                        value={data.sampling_time}
                        disabled={!data.historical || data.has_sampling_time}
                        onChange={(evt) => onChange_sampleValue(evt)}
                        required
                      />
                    </td>
                  </tr>
                  {Object.entries(data.sampling_point.methods)
                    .filter(([m, _]) => data.analyzable_methods.includes(m))
                    .map(([m, method], m_idx) => (
                      <React.Fragment key={`m${m_idx}`}>
                        {m_idx > 0 && (
                          <tr key={`m${m_idx}`}>
                            <MethodColumnSpanner {...methodSpannerParams(m)} />
                            <MethodActionsColumn method={method?.name} />
                          </tr>
                        )}

                        {method?.open &&
                          [...method?.test_items]
                            // While sorting, replace null values with Infinity so they are automatically pushed below
                            // any values with a valid display_order
                            .sort((a, b) => (a.display_order ?? Infinity) - (b.display_order ?? Infinity))
                            .map((ti, ti_idx) => (
                              <tr key={`m${m_idx}_ti${ti_idx}`}>
                                {testItemColumns(
                                  isNSView,
                                  ti,
                                  `m${m_idx}_ti${ti_idx}`,
                                  isNSView,
                                  false,
                                  true,
                                  method?.slims,
                                  (event, test_item) => {
                                    let smp_data = Object.assign({}, data)
                                    const smp_method = smp_data.sampling_point.testitem_set.find(
                                      (ti) => ti.id === test_item.id,
                                    ).source_tag.source.name
                                    smp_data.sampling_point.methods[smp_method]["test_items"].find(
                                      (ti) => ti.id === test_item.id,
                                    ).value = event.target.value
                                    setData(smp_data)
                                  },
                                )}
                              </tr>
                            ))}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
              <div style={{ marginTop: "10px" }} align={"center"}>
                {status_save === API_STATUS.ERROR ? (
                  <div className={"text-danger"}>
                    <FormattedMessage
                      id={"datatransfer.error.message"}
                      description={"Something Went Wrong Message"}
                    />
                  </div>
                ) : status_save === API_STATUS.LOADING ? (
                  <div>
                    <div className={"spinner-border text-primary text-center"} role={"status"} />
                    &nbsp;&nbsp;
                    <FormattedMessage id={"general.saving"} description={"Saving Message"} />
                    ...
                  </div>
                ) : (
                  <>
                    <Button variant={"primary"} type={"submit"}>
                      <span className={"fa fa-floppy-o text_white"} />
                      &nbsp;
                      <FormattedMessage id={"general.save"} description={"Save Button"} />
                    </Button>
                  </>
                )}
              </div>
            </form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          style={{ marginLeft: "auto" }}
          variant={"outline-primary"}
          onClick={() => {
            onHide()
            updateMetrics(init_status)
          }}
        >
          <FormattedMessage id={"general.closeButton"} description={"Close Modal Button"} />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default withSampleConfig(ModalEnterData)

ModalEnterData.propTypes = {
  sample_current_data: PropTypes.array.isRequired, // SampleConfigProvider - List of objs with the current data for TestItems for this Sample
  sample_source_types: PropTypes.array.isRequired, // SampleConfigProvider - List of objs representing all possible sample source types
  stopSampleRound: PropTypes.func.isRequired, // SampleConfigProvider - Function
  updateMetrics: PropTypes.func.isRequired, // SampleConfigProvider - Function
  sample_current_data_status: PropTypes.string.isRequired, // SampleConfigProvider - API_STATUS (String)
  sample_round_stop_status: PropTypes.string.isRequired, // SampleConfigProvider - API_STATUS (String)
  sample: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
}
